import request from 'app/api/request';
import ServerErrorHandler from 'app/ErrorHandler';
import { useQueryClient, useMutation, useQuery, UseQueryResult, UseMutationResult } from 'react-query';
import { dispatchMessage, EventType } from 'app/useToasts';
import { i18n } from 'app/utils/i18n';
import {
  DocumentTaxonomyConfig,
  DocumentTaxonomyConfigTaxonomy,
  DocumentTaxonomyConfigContext,
  DocumentTaxonomyConfigCurrency,
  CreateDocumentTaxonomyConfigInput,
  UpdateDocumentTaxonomyConfigInput,
  AddTaxonomyToConfigInput,
  RemoveTaxonomyFromConfigInput,
  AddContextToConfigInput,
  RemoveContextFromConfigInput,
  SetActiveTaxonomyInput,
  AddCurrencyToConfigInput,
  RemoveCurrencyFromConfigInput,
  SetActiveCurrencyInput,
  SetActiveContextInput,
  SuccessResponse,
  TagCountAttribute,
  TagAttributeCounts,
} from './types';
import { taxonomyQueryKeys } from './taxonomy';
import { tagQueryKeys } from './tags';

const showSuccessToast = (messageKey: string) => {
  dispatchMessage(EventType.ADD_MESSAGE, { type: 'success', text: i18n(messageKey), removeTime: 4000 });
};

export const documentTaxonomyConfigQueryKeys = {
  configByDocument: 'DOC_TAXONOMY_CONFIG_BY_DOCUMENT',
  tagCounts: 'DOC_TAXONOMY_CONFIG_TAG_COUNTS',
} as const;

export function useDocumentTaxonomyConfig(documentSuperId: number | undefined): UseQueryResult<DocumentTaxonomyConfig | null> {
  return useQuery(
    [documentTaxonomyConfigQueryKeys.configByDocument, documentSuperId],
    async () => {
      const res = await request.get(`/gaby/document-taxonomy-config/document/${documentSuperId}`).catch((err: { status: number }) => {
        if (err.status === 404) {
          return null;
        }
        throw err;
      });
      return (res?.data as DocumentTaxonomyConfig) || null;
    },
    {
      enabled: !!documentSuperId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      onError: ServerErrorHandler,
    }
  );
}

export function useCreateDocumentTaxonomyConfig(): UseMutationResult<DocumentTaxonomyConfig, unknown, CreateDocumentTaxonomyConfigInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async (input: CreateDocumentTaxonomyConfigInput) => {
      const res = await request.post('/gaby/document-taxonomy-config/', input);
      return res?.data as DocumentTaxonomyConfig;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([documentTaxonomyConfigQueryKeys.configByDocument, variables.documentSuperId]);
      },
    }
  );
}

export function useUpdateDocumentTaxonomyConfig(): UseMutationResult<SuccessResponse, unknown, UpdateDocumentTaxonomyConfigInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ configId, silent, ...configData }: UpdateDocumentTaxonomyConfigInput) => {
      const res = await request.put(`/gaby/document-taxonomy-config/${configId}`, configData);
      return res?.data as SuccessResponse;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: (_data, variables) => {
        if (!variables.silent) showSuccessToast('tag.config.updated.success');
        queryClient.invalidateQueries([documentTaxonomyConfigQueryKeys.configByDocument]);
      },
    }
  );
}

export function useDeleteDocumentTaxonomyConfig(): UseMutationResult<SuccessResponse, unknown, { configId: string; documentSuperId?: number }> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ configId }: { configId: string; documentSuperId?: number }) => {
      const res = await request.del(`/gaby/document-taxonomy-config/${configId}`);
      return res?.data as SuccessResponse;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([documentTaxonomyConfigQueryKeys.configByDocument, variables.documentSuperId]);
      },
    }
  );
}

export function useAddTaxonomyToConfig(): UseMutationResult<DocumentTaxonomyConfigTaxonomy, unknown, AddTaxonomyToConfigInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ configId, taxonomyId }: AddTaxonomyToConfigInput) => {
      const res = await request.post(`/gaby/document-taxonomy-config/${configId}/taxonomies`, { taxonomyId });
      return res?.data as DocumentTaxonomyConfigTaxonomy;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: () => {
        showSuccessToast('tag.config.taxonomy.added');
        queryClient.invalidateQueries([documentTaxonomyConfigQueryKeys.configByDocument]);
        queryClient.invalidateQueries([taxonomyQueryKeys.activeTaxonomy]);
        queryClient.invalidateQueries([taxonomyQueryKeys.presentationTree]);
      },
    }
  );
}

export function useRemoveTaxonomyFromConfig(): UseMutationResult<SuccessResponse, unknown, RemoveTaxonomyFromConfigInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ configId, taxonomyId }: RemoveTaxonomyFromConfigInput) => {
      const res = await request.del(`/gaby/document-taxonomy-config/${configId}/taxonomies/${taxonomyId}`);
      return res?.data as SuccessResponse;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: () => {
        showSuccessToast('tag.config.taxonomy.removed');
        queryClient.invalidateQueries([documentTaxonomyConfigQueryKeys.configByDocument]);
        queryClient.invalidateQueries([documentTaxonomyConfigQueryKeys.tagCounts]);
        queryClient.invalidateQueries([taxonomyQueryKeys.activeTaxonomy]);
        queryClient.invalidateQueries([taxonomyQueryKeys.presentationTree]);
        queryClient.invalidateQueries([tagQueryKeys.tagsByDocument]);
        queryClient.invalidateQueries([tagQueryKeys.tagsByElement]);
      },
    }
  );
}

export function useAddContextToConfig(): UseMutationResult<DocumentTaxonomyConfigContext, unknown, AddContextToConfigInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ configId, contextId }: AddContextToConfigInput) => {
      const res = await request.post(`/gaby/document-taxonomy-config/${configId}/contexts`, { contextId });
      return res?.data as DocumentTaxonomyConfigContext;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: () => {
        showSuccessToast('tag.config.date.added');
        queryClient.invalidateQueries([documentTaxonomyConfigQueryKeys.configByDocument]);
      },
    }
  );
}

export function useRemoveContextFromConfig(): UseMutationResult<SuccessResponse, unknown, RemoveContextFromConfigInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ configId, contextId }: RemoveContextFromConfigInput) => {
      const res = await request.del(`/gaby/document-taxonomy-config/${configId}/contexts/${contextId}`);
      return res?.data as SuccessResponse;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: () => {
        showSuccessToast('tag.config.date.removed');
        queryClient.invalidateQueries([documentTaxonomyConfigQueryKeys.configByDocument]);
      },
    }
  );
}

export function useSetActiveTaxonomy(): UseMutationResult<SuccessResponse, unknown, SetActiveTaxonomyInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ configId, taxonomyId }: SetActiveTaxonomyInput) => {
      const res = await request.put(`/gaby/document-taxonomy-config/${configId}/active-taxonomy`, { taxonomyId });
      return res?.data as SuccessResponse;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: () => {
        showSuccessToast('tag.config.taxonomy.active.updated');
        queryClient.invalidateQueries([documentTaxonomyConfigQueryKeys.configByDocument]);
        queryClient.invalidateQueries([taxonomyQueryKeys.activeTaxonomy]);
        queryClient.invalidateQueries([taxonomyQueryKeys.presentationTree]);
      },
    }
  );
}

export function useAddCurrencyToConfig(): UseMutationResult<DocumentTaxonomyConfigCurrency, unknown, AddCurrencyToConfigInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ configId, currency }: AddCurrencyToConfigInput) => {
      const res = await request.post(`/gaby/document-taxonomy-config/${configId}/currencies`, { currency });
      return res?.data as DocumentTaxonomyConfigCurrency;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: () => {
        showSuccessToast('tag.config.currency.added');
        queryClient.invalidateQueries([documentTaxonomyConfigQueryKeys.configByDocument]);
      },
    }
  );
}

export function useRemoveCurrencyFromConfig(): UseMutationResult<SuccessResponse, unknown, RemoveCurrencyFromConfigInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ configId, currencyId }: RemoveCurrencyFromConfigInput) => {
      const res = await request.del(`/gaby/document-taxonomy-config/${configId}/currencies/${currencyId}`);
      return res?.data as SuccessResponse;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: () => {
        showSuccessToast('tag.config.currency.removed');
        queryClient.invalidateQueries([documentTaxonomyConfigQueryKeys.configByDocument]);
      },
    }
  );
}

export function useSetActiveCurrency(): UseMutationResult<SuccessResponse, unknown, SetActiveCurrencyInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ configId, currencyId }: SetActiveCurrencyInput) => {
      const res = await request.put(`/gaby/document-taxonomy-config/${configId}/active-currency`, { currencyId });
      return res?.data as SuccessResponse;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: () => {
        showSuccessToast('tag.config.currency.active.updated');
        queryClient.invalidateQueries([documentTaxonomyConfigQueryKeys.configByDocument]);
      },
    }
  );
}

export function useSetActiveContext(): UseMutationResult<SuccessResponse, unknown, SetActiveContextInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ configId, contextId }: SetActiveContextInput) => {
      const res = await request.put(`/gaby/document-taxonomy-config/${configId}/active-context`, { contextId });
      return res?.data as SuccessResponse;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: () => {
        showSuccessToast('tag.config.date.active.updated');
        queryClient.invalidateQueries([documentTaxonomyConfigQueryKeys.configByDocument]);
      },
    }
  );
}

export function useTagAttributeCounts(configId: string | undefined, attributes: TagCountAttribute[]): UseQueryResult<TagAttributeCounts> {
  const attributesKey = attributes.sort().join(',');
  return useQuery(
    [documentTaxonomyConfigQueryKeys.tagCounts, configId, attributesKey],
    async () => {
      const res = await request.get(`/gaby/document-taxonomy-config/${configId}/tag-counts?attributes=${attributesKey}`);
      return (res?.data as TagAttributeCounts) || {};
    },
    {
      enabled: !!configId && attributes.length > 0,
      onError: ServerErrorHandler,
    }
  );
}
